import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ErrorBoundary } from 'react-error-boundary';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import type { ZodError } from 'zod';
import { AdminRoute } from './components/admin/AdminRoute';
import { ScrollToTop } from './components/layout/ScrollToTop';
import { RouteProgressBar } from './components/loading/RouteProgressBar';
import { FirstLoginGuard } from './components/auth/FirstLoginGuard';
import { RootRoute } from './components/routing/RootRoute';
import { useAuthStore } from './stores/auth-store';
import { useTranslation } from './locales';
import { applyAnalyticsOptOut } from './services/analytics-service';
import { auth, db } from './lib/firebase';
import { normalizeHouseholdDoc } from './lib/household';
import { isNewlyCreatedUser, isFirestoreOffline } from './lib/household-log';
import { logError, logBreadcrumb } from './lib/sentry';

// Story 13.14: Route-based code splitting. Pages use named exports, so lazy()
// needs the `.then(m => ({ default: m.X }))` wrapper.
const SignupPage = lazy(() =>
  import('./pages/auth/SignupPage').then((m) => ({ default: m.SignupPage }))
);
const LoginPage = lazy(() =>
  import('./pages/auth/LoginPage').then((m) => ({ default: m.LoginPage }))
);
const ChildProfilesPage = lazy(() =>
  import('./pages/auth/ChildProfilesPage').then((m) => ({ default: m.ChildProfilesPage }))
);
// Story 23.2: first-time social onboarding (consent + household creation)
const CompleteSignupPage = lazy(() =>
  import('./pages/auth/CompleteSignupPage').then((m) => ({ default: m.CompleteSignupPage }))
);
// Story 23.4: forgot-password request page (replaces the ComingSoonPage stub)
const ForgotPasswordPage = lazy(() =>
  import('./pages/auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage }))
);
// Story 23.14: custom in-app reset-password page (target of the reset email link,
// via the Firebase Console "Customize action URL" setting)
const ResetPasswordPage = lazy(() =>
  import('./pages/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage }))
);
const GuidedStoryPage = lazy(() =>
  import('./pages/GuidedStoryPage').then((m) => ({ default: m.GuidedStoryPage }))
);
// Story 20.1: `/` now branches on auth state inside RootRoute, which lazy-loads
// HomePage (authenticated dashboard) or PublicHomePage (anonymous landing).
const OriginalsCatalogPage = lazy(() =>
  import('./pages/originals/OriginalsCatalogPage').then((m) => ({
    default: m.OriginalsCatalogPage,
  }))
);
const OriginalsStoryPage = lazy(() =>
  import('./pages/originals/OriginalsStoryPage').then((m) => ({ default: m.OriginalsStoryPage }))
);
const OriginalsCharactersPage = lazy(() =>
  import('./pages/originals/OriginalsCharactersPage').then((m) => ({
    default: m.OriginalsCharactersPage,
  }))
);
const OriginalsCharacterPage = lazy(() =>
  import('./pages/originals/OriginalsCharacterPage').then((m) => ({
    default: m.OriginalsCharacterPage,
  }))
);
const CreateStoryPage = lazy(() =>
  import('./pages/CreateStoryPage').then((m) => ({ default: m.CreateStoryPage }))
);
const CreateCharacterPage = lazy(() =>
  import('./pages/CreateCharacterPage').then((m) => ({ default: m.CreateCharacterPage }))
);
const CharacterLibraryPage = lazy(() =>
  import('./pages/CharacterLibraryPage').then((m) => ({ default: m.CharacterLibraryPage }))
);
const StoryLibraryPage = lazy(() =>
  import('./pages/StoryLibraryPage').then((m) => ({ default: m.StoryLibraryPage }))
);
// Story 20.20: in-service newsletter subscription page (login-gated)
const NewsletterPage = lazy(() =>
  import('./pages/NewsletterPage').then((m) => ({ default: m.NewsletterPage }))
);
const StoryReaderPage = lazy(() =>
  import('./pages/StoryReaderPage').then((m) => ({ default: m.StoryReaderPage }))
);
const SettingsPage = lazy(() =>
  import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage }))
);
const TipsPage = lazy(() => import('./pages/TipsPage').then((m) => ({ default: m.TipsPage })));
const CollectionPage = lazy(() =>
  import('./pages/CollectionPage').then((m) => ({ default: m.CollectionPage }))
);
const ChangePasswordPage = lazy(() =>
  import('./pages/ChangePasswordPage').then((m) => ({ default: m.ChangePasswordPage }))
);
const TermsPage = lazy(() =>
  import('./pages/legal/TermsPage').then((m) => ({ default: m.TermsPage }))
);
const PrivacyPage = lazy(() =>
  import('./pages/legal/PrivacyPage').then((m) => ({ default: m.PrivacyPage }))
);
const AboutPage = lazy(() =>
  import('./pages/legal/AboutPage').then((m) => ({ default: m.AboutPage }))
);
const HelpPage = lazy(() =>
  import('./pages/legal/HelpPage').then((m) => ({ default: m.HelpPage }))
);
const AdminLoginPage = lazy(() =>
  import('./pages/admin/AdminLoginPage').then((m) => ({ default: m.AdminLoginPage }))
);
const AdminDashboardPage = lazy(() =>
  import('./pages/admin/AdminDashboardPage').then((m) => ({ default: m.AdminDashboardPage }))
);
const AdminApiMonitoringPage = lazy(() =>
  import('./pages/admin/AdminApiMonitoringPage').then((m) => ({
    default: m.AdminApiMonitoringPage,
  }))
);
const AdminFeedbackPage = lazy(() =>
  import('./pages/admin/AdminFeedbackPage').then((m) => ({ default: m.AdminFeedbackPage }))
);
const AdminGenerationStatsPage = lazy(() =>
  import('./pages/admin/AdminGenerationStatsPage').then((m) => ({
    default: m.AdminGenerationStatsPage,
  }))
);
// Story 11.34: StyleComparisonPage demo removed
// Story 11.36: CoverSpikePage demo removed (spike complete, migrated to production)
// S18.14: VoiceSpikePage removed (spike complete, migrated to production)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10 * 60 * 1000, // 10 minutes (TAD spec)
      retry: 2,
    },
  },
});

function ErrorFallback({ error }: { error: Error }) {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="bg-error-50 border border-error-200 rounded-lg p-6 max-w-md">
        <h2 className="text-lg font-semibold text-error-800 mb-2">{t('errors.boundary.title')}</h2>
        <p className="text-error-700 text-sm">{error.message}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-error-600 text-white rounded-lg hover:bg-error-700"
        >
          {t('errors.boundary.reloadButton')}
        </button>
      </div>
    </div>
  );
}

// HomePage moved to pages/HomePage.tsx (Story 8.2)
// StoriesPage placeholder removed - replaced by StoryLibraryPage (Epic 5)

/**
 * Analytics Opt-Out Monitor
 * Applies analytics opt-out preference when household data loads (FR71)
 */
function AnalyticsOptOutMonitor() {
  const household = useAuthStore((state) => state.household);

  useEffect(() => {
    if (household?.preferences) {
      const optOut = household.preferences.analyticsOptOut ?? false;
      applyAnalyticsOptOut(optOut);
    }
  }, [household]);

  return null;
}

function App() {
  const setUser = useAuthStore((state) => state.setUser);
  const setHousehold = useAuthStore((state) => state.setHousehold);
  const setLoading = useAuthStore((state) => state.setLoading);

  // Listen for auth state changes on app initialization (Story 2.8)
  // Restores user session when user returns with valid Firebase token
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // User is signed in - restore session
        setUser(user);

        // Fetch household data
        try {
          const householdDoc = await getDoc(doc(db, 'households', user.uid));
          if (householdDoc.exists()) {
            // Story 23.9: single shared hydration routine — converts every
            // Timestamp→Date (incl. feedbackState.*), normalizes the legacy
            // consentTimestamps shape, applies defaults, and runs safeParse.
            // Replaces the former large inline block (PATTERN-001 unification).
            // QA fix OBS-23.9-1: capture the Zod error via the routine's hook so
            // the failure log keeps the field-level detail the old inline block had.
            let validationError: ZodError | undefined;
            const household = normalizeHouseholdDoc(householdDoc.data(), (error) => {
              validationError = error;
            });

            if (household) {
              setHousehold(household);
            } else {
              // Invalid household data structure - log error and set null
              logError(new Error('Invalid household data structure'), {
                userId: user.uid,
                validationErrors: validationError?.format(),
              });
              setHousehold(null);
            }
          } else if (isNewlyCreatedUser(user.metadata)) {
            // Expected: a just-created account has no household yet — a first-time
            // social sign-in routes to S23.2 consent/onboarding. Breadcrumb, not an
            // error, so launch-time signups don't spam Sentry. [Sentry 131606349]
            logBreadcrumb('Household not found for newly-created user — routing to onboarding', {
              userId: user.uid,
            });
            setHousehold(null);
          } else {
            // Established account with no household doc — a genuine problem (a doc
            // that failed to create / was lost). Keep at error severity.
            logError(new Error('Household document not found'), { userId: user.uid });
            setHousehold(null);
          }
        } catch (error) {
          if (isFirestoreOffline(error)) {
            // Transient offline blip — the SDK auto-reconnects. Benign network
            // noise (common on mobile), so a breadcrumb, not an error. [Sentry 131603075]
            logBreadcrumb('Household fetch skipped — Firestore temporarily offline', {
              userId: user.uid,
            });
          } else {
            // Real network / Firestore failure — log at error severity.
            logError(error instanceof Error ? error : new Error('Failed to fetch household'), {
              userId: user.uid,
            });
          }
          setHousehold(null);
        }
      } else {
        // User is signed out
        setUser(null);
        setHousehold(null);
      }

      setLoading(false);
    });

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, [setUser, setHousehold, setLoading]);

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <QueryClientProvider client={queryClient}>
        <AnalyticsOptOutMonitor />
        <BrowserRouter
          future={{
            // Story 13.14: wrap route navigations in React.startTransition so
            // the previous page stays visible while the next lazy chunk loads.
            // Without this, route changes unmount the old page and Suspense's
            // fallback renders against a blank viewport — visible white flash.
            // With it, the RouteProgressBar only appears on genuinely slow
            // loads (cold cache / throttled network), and warm navigations
            // feel instant.
            v7_startTransition: true,
            // Quiet the related future-flag warning. No behavioural change for
            // this app (we don't rely on splat-relative paths today).
            v7_relativeSplatPath: true,
          }}
        >
          <ScrollToTop />
          <FirstLoginGuard>
            <Suspense fallback={<RouteProgressBar />}>
              <Routes>
                {/* Story 20.1: `/` branches anonymous → PublicHomePage,
                    authenticated → dashboard. FirstLoginGuard no-ops for
                    anonymous (household is null), so the public branch is
                    never redirected. */}
                <Route path="/" element={<RootRoute />} />
                {/* Story 20.1: public Originals routes (placeholders → S20.5/S20.6) */}
                <Route path="/originals" element={<OriginalsCatalogPage />} />
                {/* S20.16: static character routes declared BEFORE /originals/:slug
                    so they win over the dynamic story slug (React Router ranks the
                    static `characters` segment above the `:slug` param). */}
                <Route path="/originals/characters" element={<OriginalsCharactersPage />} />
                <Route path="/originals/characters/:slug" element={<OriginalsCharacterPage />} />
                <Route path="/originals/:slug" element={<OriginalsStoryPage />} />
                <Route path="/auth/login" element={<LoginPage />} />
                <Route path="/auth/signup" element={<SignupPage />} />
                {/* Story 23.2: first-time social users land here for consent +
                    household creation before child-profiles → onboarding. */}
                <Route path="/auth/complete-signup" element={<CompleteSignupPage />} />
                <Route path="/auth/child-profiles" element={<ChildProfilesPage />} />
                <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
                {/* Story 23.14: reset-link target — the full-policy in-app reset page */}
                <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
                {/* Story 7A.4: Password change for pre-created beta accounts */}
                <Route path="/change-password" element={<ChangePasswordPage />} />
                {/* Epic 17: /onboarding now renders guided CreateStoryPage */}
                <Route path="/onboarding" element={<GuidedStoryPage />} />
                <Route path="/stories" element={<StoryLibraryPage />} />
                <Route path="/library" element={<StoryLibraryPage />} />
                <Route path="/story/:storyId" element={<StoryReaderPage />} />
                <Route path="/story/:storyId/episode/:episodeIndex" element={<StoryReaderPage />} />
                <Route path="/characters" element={<CharacterLibraryPage />} />
                <Route path="/tips" element={<TipsPage />} />
                <Route path="/uutiskirje" element={<NewsletterPage />} />
                <Route path="/collection" element={<CollectionPage />} />
                <Route path="/create-story" element={<CreateStoryPage />} />
                <Route path="/create-character" element={<CreateCharacterPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/help" element={<HelpPage />} />
                {/* Admin routes (Story 7A.1, 7A.2, 7C.3) */}
                <Route path="/admin/login" element={<AdminLoginPage />} />
                <Route
                  path="/admin/dashboard"
                  element={
                    <AdminRoute>
                      <AdminDashboardPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/api-monitoring"
                  element={
                    <AdminRoute>
                      <AdminApiMonitoringPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/generation-stats"
                  element={
                    <AdminRoute>
                      <AdminGenerationStatsPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/feedback"
                  element={
                    <AdminRoute>
                      <AdminFeedbackPage />
                    </AdminRoute>
                  }
                />
              </Routes>
            </Suspense>
          </FirstLoginGuard>
          <Toaster
            position="top-center"
            toastOptions={{
              duration: 4000,
              style: {
                borderRadius: '8px',
                padding: '12px 16px',
              },
            }}
          />
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
